import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';
import { 
    Brain, 
    Target, 
    TrendingUp, 
    BookOpen, 
    Zap,
    CheckCircle,
    ArrowRight,
    Stethoscope,
    GraduationCap
} from 'lucide-react';

export default function Landing() {
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();

    const features = [
        {
            icon: Brain,
            title: "AI-Generated Questions",
            description: "High-quality NBME-style questions generated by advanced AI, tailored to pre-clinical subjects."
        },
        {
            icon: Target,
            title: "Pattern Recognition",
            description: "Identify your weak areas with detailed analytics on missed questions and common mistakes."
        },
        {
            icon: TrendingUp,
            title: "Performance Tracking",
            description: "Track your progress over time with subject-wise and difficulty-based analytics."
        },
        {
            icon: BookOpen,
            title: "Spaced Repetition",
            description: "Optimize your learning with smart review schedules for questions you've missed."
        }
    ];

    const subjects = [
        "Anatomy", "Physiology", "Biochemistry", 
        "Pathology", "Pharmacology", "Microbiology",
        "PDCM", "Epidemiology/Biostatistics", "Histology", "Behavioral Sciences"
    ];

    const handleGetStarted = () => {
        if (isAuthenticated) {
            navigate('/dashboard');
        } else {
            navigate('/register');
        }
    };

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-2">
                            <Stethoscope className="h-8 w-8 text-indigo-600" />
                            <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                                MedPrep NBME & CBSE
                            </span>
                        </div>
                        <div className="flex items-center gap-4">
                            {isAuthenticated ? (
                                <Button 
                                    onClick={() => navigate('/dashboard')}
                                    data-testid="nav-dashboard-btn"
                                >
                                    Dashboard
                                </Button>
                            ) : (
                                <>
                                    <Link to="/login">
                                        <Button variant="ghost" data-testid="nav-login-btn">
                                            Sign In
                                        </Button>
                                    </Link>
                                    <Link to="/register">
                                        <Button data-testid="nav-register-btn">
                                            Get Started
                                        </Button>
                                    </Link>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="pt-32 pb-20 px-4 hero-gradient">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div className="animate-fade-in">
                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-100 text-indigo-700 text-sm font-medium mb-6">
                                <Zap className="h-4 w-4" />
                                AI-Powered Medical Education
                            </div>
                            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight mb-6" style={{ fontFamily: 'Manrope' }}>
                                Master NBME & CBSE with{' '}
                                <span className="text-indigo-600">Intelligent</span> Practice
                            </h1>
                            <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-xl">
                                Prepare for your NBME, CBSE, and CAS exams with AI-generated questions. 
                                Track your progress, identify weak areas, and optimize your study time 
                                with smart spaced repetition and system-based practice.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Button 
                                    size="lg" 
                                    onClick={handleGetStarted}
                                    className="text-lg px-8"
                                    data-testid="hero-get-started-btn"
                                >
                                    Start Practicing Free
                                    <ArrowRight className="ml-2 h-5 w-5" />
                                </Button>
                                <Button 
                                    size="lg" 
                                    variant="outline"
                                    onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                                    data-testid="hero-learn-more-btn"
                                >
                                    Learn More
                                </Button>
                            </div>
                        </div>
                        <div className="hidden lg:block animate-slide-up">
                            <img 
                                src="https://images.unsplash.com/photo-1659353887907-000c9a92377d?w=600&h=500&fit=crop"
                                alt="Medical student studying"
                                className="rounded-2xl shadow-2xl"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Subjects */}
            <section className="py-12 bg-white border-y border-slate-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <p className="text-center text-slate-500 mb-6">Covering all pre-clinical subjects</p>
                    <div className="flex flex-wrap justify-center gap-4">
                        {subjects.map((subject) => (
                            <span 
                                key={subject}
                                className="px-4 py-2 rounded-full bg-slate-100 text-slate-700 font-medium text-sm"
                            >
                                {subject}
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features */}
            <section id="features" className="py-20 px-4">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope' }}>
                            Everything You Need to Succeed
                        </h2>
                        <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                            Our intelligent platform helps you study smarter, not harder, 
                            with features designed for medical board exam success.
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {features.map((feature, index) => (
                            <Card 
                                key={feature.title}
                                className="border-slate-200 hover:border-indigo-200 hover:shadow-lg transition-all duration-300 animate-slide-up"
                                style={{ animationDelay: `${index * 100}ms` }}
                            >
                                <CardContent className="p-6">
                                    <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center mb-4">
                                        <feature.icon className="h-6 w-6 text-indigo-600" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
                                        {feature.title}
                                    </h3>
                                    <p className="text-slate-600 text-sm leading-relaxed">
                                        {feature.description}
                                    </p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* How it Works */}
            <section className="py-20 px-4 bg-slate-900 text-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope' }}>
                            How It Works
                        </h2>
                        <p className="text-lg text-slate-300 max-w-2xl mx-auto">
                            Start improving your board exam scores in three simple steps
                        </p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            { step: "01", title: "Choose Your Subject", description: "Select from anatomy, physiology, biochemistry, and more pre-clinical subjects." },
                            { step: "02", title: "Practice Questions", description: "Answer AI-generated NBME-style questions with detailed explanations." },
                            { step: "03", title: "Track & Improve", description: "Review analytics, identify weak areas, and use spaced repetition to master difficult topics." }
                        ].map((item, index) => (
                            <div key={item.step} className="text-center">
                                <div className="text-5xl font-bold text-indigo-400 mb-4" style={{ fontFamily: 'Manrope' }}>
                                    {item.step}
                                </div>
                                <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Manrope' }}>
                                    {item.title}
                                </h3>
                                <p className="text-slate-400">
                                    {item.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-20 px-4 bg-indigo-600">
                <div className="max-w-4xl mx-auto text-center">
                    <GraduationCap className="h-16 w-16 text-white/80 mx-auto mb-6" />
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope' }}>
                        Ready to Ace Your Boards?
                    </h2>
                    <p className="text-xl text-indigo-100 mb-8">
                        Join thousands of medical students using MedPrep AI to prepare smarter.
                    </p>
                    <Button 
                        size="lg" 
                        variant="secondary"
                        onClick={handleGetStarted}
                        className="text-lg px-8 bg-white text-indigo-600 hover:bg-indigo-50"
                        data-testid="cta-get-started-btn"
                    >
                        Get Started Free
                        <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-8 px-4 bg-slate-900 text-slate-400">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                        <Stethoscope className="h-6 w-6 text-indigo-400" />
                        <span className="text-white font-semibold" style={{ fontFamily: 'Manrope' }}>MedPrep NBME & CBSE</span>
                    </div>
                    <p className="text-sm">
                        © {new Date().getFullYear()} MedPrep. Built for medical students.
                    </p>
                </div>
            </footer>
        </div>
    );
}
